export interface LineDiffPart {
	kind: "same" | "added" | "removed"
	text: string
}

// Past this many cells the LCS table costs more than the card is worth.
const MAX_DIFF_CELLS = 250_000

const splitLines = (text: string) =>
	text.length === 0 ? [] : text.replace(/\r\n/g, "\n").split("\n")

export function lineDiff(before: string, after: string): LineDiffPart[] {
	const a = splitLines(before)
	const b = splitLines(after)
	if (a.length * b.length > MAX_DIFF_CELLS) {
		return [
			...a.map((text) => ({ kind: "removed" as const, text })),
			...b.map((text) => ({ kind: "added" as const, text })),
		]
	}

	const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
		new Array<number>(b.length + 1).fill(0),
	)
	for (let i = a.length - 1; i >= 0; i--) {
		for (let j = b.length - 1; j >= 0; j--) {
			lcs[i]![j] =
				a[i] === b[j]
					? lcs[i + 1]![j + 1]! + 1
					: Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!)
		}
	}

	const parts: LineDiffPart[] = []
	let i = 0
	let j = 0
	while (i < a.length && j < b.length) {
		if (a[i] === b[j]) {
			parts.push({ kind: "same", text: a[i]! })
			i++
			j++
		} else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
			parts.push({ kind: "removed", text: a[i]! })
			i++
		} else {
			parts.push({ kind: "added", text: b[j]! })
			j++
		}
	}
	while (i < a.length) parts.push({ kind: "removed", text: a[i++]! })
	while (j < b.length) parts.push({ kind: "added", text: b[j++]! })
	return parts
}

export type CompactDiffPart = LineDiffPart | { kind: "skip"; count: number }

// Long runs of unchanged lines fold into a count, keeping `context` lines of
// each run next to a change.
export function compactLineDiff(
	parts: LineDiffPart[],
	context = 1,
): CompactDiffPart[] {
	const changed = parts.map((p) => p.kind !== "same")
	const keep = parts.map((_, i) => {
		if (changed[i]) return true
		for (let d = 1; d <= context; d++) {
			if (changed[i - d] || changed[i + d]) return true
		}
		return false
	})
	const out: CompactDiffPart[] = []
	parts.forEach((part, i) => {
		if (keep[i]) {
			out.push(part)
			return
		}
		const last = out.at(-1)
		if (last?.kind === "skip") last.count++
		else out.push({ kind: "skip", count: 1 })
	})
	return out
}
